import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { api } from '../lib/api';
import LanguageToggle from '../components/LanguageToggle';
import UserAvatar from '../components/UserAvatar';
import BottomNav from '../components/BottomNav';
import { ChevronLeft, Upload } from 'lucide-react';

export default function EditProfilePage() {
  const { user, refresh, setUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    country: user?.country || '',
    state: user?.state || '',
    city: user?.city || '',
  });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch('/profile/me', form);
      setUser(data);
      toast.success(t('profile.saved'));
      navigate('/me');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally { setBusy(false); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/profile/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fresh = await refresh();
      if (fresh) toast.success(t('profile.saved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally { setAvatarBusy(false); }
  };

  return (
    <div className="min-h-screen bg-aura-bg pb-24 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6 pb-2">
        <button onClick={() => navigate(-1)} className="text-aura-textDim hover:text-white flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} /> {t('common.back')}
        </button>
        <LanguageToggle />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4">
        <h1 className="font-display font-black text-3xl aura-text-gradient mb-6">{t('profile.edit')}</h1>

        <section className="aura-card p-5 mb-5 flex items-center gap-4">
          <UserAvatar user={user} size="lg" />
          <div className="flex-1">
            <div className="text-sm text-white/80 mb-2">@{user?.username}</div>
            <button
              type="button"
              disabled={avatarBusy}
              onClick={() => fileRef.current?.click()}
              className="aura-btn-ghost !py-2 !px-3 text-sm"
              data-testid="btn-upload-avatar"
            >
              <Upload size={14} />{avatarBusy ? t('common.loading') : t('profile.uploadAvatar')}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatar} data-testid="input-avatar-file" />
          </div>
        </section>

        <form onSubmit={save} className="space-y-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest font-display font-semibold text-aura-textDim block mb-1.5">{t('auth.name')}</span>
            <input data-testid="edit-name" className="aura-input" required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest font-display font-semibold text-aura-textDim block mb-1.5">{t('profile.bio')}</span>
            <textarea data-testid="edit-bio" className="aura-input min-h-[90px] py-2" maxLength={280} placeholder={t('profile.bioPlaceholder')} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest font-display font-semibold text-aura-textDim block mb-1.5">{t('auth.country')}</span>
              <input data-testid="edit-country" className="aura-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest font-display font-semibold text-aura-textDim block mb-1.5">{t('auth.state')}</span>
              <input data-testid="edit-state" className="aura-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest font-display font-semibold text-aura-textDim block mb-1.5">{t('auth.city')}</span>
            <input data-testid="edit-city" className="aura-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </label>

          <button disabled={busy} data-testid="btn-save-profile" className="aura-btn-primary w-full h-12 text-base disabled:opacity-60">
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </form>
      </main>

      <BottomNav />
    </div>
  );
}
